import {
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileText,
  LayoutGrid,
  Megaphone,
  MessageSquare,
  Store,
  UserPlus,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { DirectMessageModal } from '@/modules/messages/components/DirectMessageModal'
import { useMessages } from '@/modules/messages/hooks/useMessages'
import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import NotificationBell from '@/shared/components/NotificationBell'
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { getRoleLabel, ROLES } from '@/shared/constants/roles'
import { ROUTES } from '@/shared/constants/routes'
import { cn } from '@/shared/lib/utils'

const HEADER = {
  height: 68,
  logo: 36,
  titleSize: 14.5,
  subtitleSize: 9.5,
  navPillHeight: 42,
  navItemHeight: 34,
  navFontSize: 13,
  navPadX: 16,
  avatar: 32,
  profilePillHeight: 38,
}

const NAV_BY_ROLE = {
  [ROLES.ADMIN]: [
    { to: ROUTES.ADMIN, label: 'Dashboard', end: true, icon: LayoutGrid },
    { to: ROUTES.ADMIN_LEADS, label: 'Leads', icon: UserPlus },
    { to: ROUTES.ADMIN_QUOTES, label: 'Quotes', icon: FileText },
    { to: ROUTES.ADMIN_SALES, label: 'Sales', icon: Users },
    { to: ROUTES.ADMIN_CUSTOMERS, label: 'Customer', icon: Users },
    { to: ROUTES.ADMIN_DCR, label: 'DCR', icon: FileText },
    { to: ROUTES.ADMIN_MARKETING, label: 'Marketing', icon: Megaphone },
    { to: ROUTES.ADMIN_PURCHASE, label: 'Purchase', icon: Users },
  ],
  [ROLES.SALES]: [
    { to: ROUTES.SALES, label: 'Dashboard', end: true, icon: LayoutGrid },
    { to: ROUTES.SALES_LEADS, label: 'Leads', icon: UserPlus },
    { to: ROUTES.SALES_QUOTES, label: 'Quotes', icon: FileText },
    { to: ROUTES.SALES_CUSTOMERS, label: 'Customer', icon: Users },
    { to: ROUTES.SALES_DCR, label: 'DCR', icon: FileText },
  ],
  [ROLES.MARKETING]: [
    { to: ROUTES.MARKETING, label: 'Dashboard', end: true, icon: LayoutGrid },
    { to: ROUTES.MARKETING_LEADS, label: 'Leads', icon: UserPlus },
  ],
  [ROLES.PURCHASE]: [{ to: ROUTES.PURCHASE, label: 'Purchase', icon: Users }],
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function NavItems({
  items,
  compact = false,
  unreadCounts = {},
  hasUnreadPOComments = false,
}) {
  const location = useLocation()

  return items.map((item) => {
    const Icon = item.icon
    const isExactActive = item.end
      ? location.pathname === item.to
      : location.pathname === item.to ||
        location.pathname.startsWith(`${item.to}/`)

    let showNavBadge = false

    if (item.to === ROUTES.ADMIN_SALES) {
      showNavBadge = (unreadCounts?.byRole?.sales || 0) > 0
    } else if (item.to === ROUTES.ADMIN_MARKETING) {
      showNavBadge = (unreadCounts?.byRole?.marketing || 0) > 0
    } else if (item.to === ROUTES.ADMIN_PURCHASE) {
      const hasUnreadPurchaseDMs = (unreadCounts?.byRole?.purchase || 0) > 0
      showNavBadge = hasUnreadPurchaseDMs || hasUnreadPOComments
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        preventScrollReset
        onClick={(event) => {
          if (isExactActive) {
            event.preventDefault()
          }
        }}
        className={({ isActive }) =>
          cn(
            'relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full no-underline transition-colors',
            compact ? 'min-h-11 flex-1 flex-col gap-0.5 px-2 py-1.5' : 'px-4',
            isActive
              ? 'bg-brand-navy text-white'
              : 'text-[#6B7280] hover:bg-black/[0.04] hover:text-[#111827]',
          )
        }
        style={{
          height: compact ? 'auto' : HEADER.navItemHeight,
          minHeight: compact ? 44 : HEADER.navItemHeight,
          fontSize: compact ? 10 : HEADER.navFontSize,
          fontWeight: 600,
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon size={compact ? 14 : 15} />
        <span>{item.label}</span>

        {showNavBadge && (
          <span className="flex size-2 rounded-full bg-red-500 ring-2 ring-white" />
        )}
      </NavLink>
    )
  })
}

function PurchaseNavItems({
  compact = false,
  activePurchaseTab,
  onTabClick,
  hasUnreadPOComments,
}) {
  const tabClass = (tab) =>
    cn(
      'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full transition-colors',
      compact
        ? 'min-h-11 flex-1 flex-col gap-0.5 rounded-xl px-2 py-1.5'
        : 'px-4',
      activePurchaseTab === tab
        ? 'bg-brand-navy text-white'
        : 'text-[#6B7280] hover:bg-black/[0.04] hover:text-[#111827]',
    )

  const tabStyle = {
    height: compact ? 'auto' : HEADER.navItemHeight,
    minHeight: compact ? 44 : HEADER.navItemHeight,
    fontSize: compact ? 10 : HEADER.navFontSize,
    fontWeight: 600,
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
  }

  return (
    <>
      <button
        type="button"
        onClick={() => onTabClick('vendors')}
        className={tabClass('vendors')}
        style={tabStyle}
        aria-current={activePurchaseTab === 'vendors' ? 'page' : undefined}
      >
        <Store size={compact ? 14 : 15} />
        <span>Vendor List</span>
      </button>

      <button
        type="button"
        onClick={() => onTabClick('orders')}
        className={tabClass('orders')}
        style={tabStyle}
        aria-current={activePurchaseTab === 'orders' ? 'page' : undefined}
      >
        <ClipboardList size={compact ? 14 : 15} />
        <span>Purchase Order</span>
        {hasUnreadPOComments && (
          <span className="flex size-2 rounded-full bg-red-500 ring-2 ring-white" />
        )}
      </button>
    </>
  )
}

export function AppNavbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, role, logout, isLoading } = useAuth()
  const { unreadCounts, fetchAdminContact } = useMessages()
  const { notifications } = useCommentsStore()
  const [adminChatOpen, setAdminChatOpen] = useState(false)
  const [adminContactUser, setAdminContactUser] = useState(null)
  const [isProfileOpen, setIsProfileOpen] = useState(false)

  const hasUnreadPOComments = notifications.some(
    (n) => (n.purchaseOrderId || n.purchaseOrder) && !n.isRead,
  )

  const navItems = NAV_BY_ROLE[role] || NAV_BY_ROLE[ROLES.ADMIN]

  const isPurchasePage =
    role === ROLES.PURCHASE ||
    location.pathname === ROUTES.PURCHASE ||
    location.pathname.startsWith(`${ROUTES.PURCHASE}/`)

  const activePurchaseTab =
    new URLSearchParams(location.search).get('tab') === 'orders'
      ? 'orders'
      : 'vendors'

  const handlePurchaseTabClick = (tab) => {
    navigate({
      pathname: location.pathname,
      search: `?tab=${tab}`,
    })
  }

  const handleLogout = async () => {
    await logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const handleOpenAdminChat = async () => {
    const admin = await fetchAdminContact()

    if (admin) {
      setAdminContactUser({
        ...admin,
        role: 'admin',
      })
      setAdminChatOpen(true)
    }
  }

  const canMessageAdmin =
    role === ROLES.SALES || role === ROLES.MARKETING || role === ROLES.PURCHASE

  return (
    <div className="sticky top-0 z-40 bg-surface-page">
      <header
        className="border-b border-black/[0.05]"
        style={{ height: HEADER.height }}
      >
        <div className="relative mx-auto flex h-full w-full max-w-[1400px] items-center justify-between gap-2 px-3 sm:px-4 md:px-6">
          {/* Logo & Role */}
          <div className="relative z-10 flex min-w-0 items-center gap-2 sm:gap-2.5">
            <img
              src="/logo.jpeg"
              alt="Superior CRM"
              className="shrink-0 rounded-full bg-white object-contain shadow-[0_6px_16px_rgba(99,102,241,0.28)]"
              style={{
                width: HEADER.logo,
                height: HEADER.logo,
              }}
            />

            <div className="min-w-0 max-w-[10rem] sm:max-w-none">
              <p
                className="truncate font-bold tracking-tight text-brand-navy"
                style={{
                  fontSize: HEADER.titleSize,
                  lineHeight: 1.15,
                }}
              >
                Superior CRM
              </p>

              <p
                className="mt-0.5 truncate font-semibold uppercase tracking-[0.08em] text-text-muted"
                style={{
                  fontSize: HEADER.subtitleSize,
                  lineHeight: 1.2,
                }}
              >
                {getRoleLabel(role)}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav
            aria-label="Primary"
            className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-0.5 overflow-hidden rounded-full border border-black/[0.06] bg-white p-1 shadow-[0_1px_3px_rgba(15,23,42,0.06)] lg:flex"
            style={{ height: HEADER.navPillHeight }}
          >
            {isPurchasePage ? (
              <PurchaseNavItems
                activePurchaseTab={activePurchaseTab}
                onTabClick={handlePurchaseTabClick}
                hasUnreadPOComments={hasUnreadPOComments}
              />
            ) : (
              <NavItems
                items={navItems}
                unreadCounts={unreadCounts}
                hasUnreadPOComments={hasUnreadPOComments}
              />
            )}
          </nav>

          {/* Right Actions / Profile */}
          <div className="relative z-10 flex shrink-0 items-center justify-end gap-2.5">
            {canMessageAdmin && (
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={handleOpenAdminChat}
                  className="rounded-full border-black/[0.06] bg-white px-3 gap-1.5 shadow-sm hover:bg-indigo-50 hover:text-indigo-600 text-xs font-semibold text-slate-700 transition-colors"
                  style={{ height: HEADER.profilePillHeight }}
                  title="Message Admin"
                >
                  <MessageSquare className="size-4 text-indigo-600" />
                  <span className="hidden sm:inline">Message Admin</span>
                </Button>

                {unreadCounts?.total > 0 && (
                  <span className="absolute -top-1 -right-1 flex min-w-4 h-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-xs animate-in fade-in">
                    {unreadCounts.total > 99 ? '99+' : unreadCounts.total}
                  </span>
                )}
              </div>
            )}

            <NotificationBell />

            <DropdownMenu open={isProfileOpen} onOpenChange={setIsProfileOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-black/[0.06] bg-white px-1.5 shadow-sm hover:bg-white"
                  style={{ height: HEADER.profilePillHeight }}
                >
                  <Avatar
                    style={{
                      width: HEADER.avatar,
                      height: HEADER.avatar,
                    }}
                  >
                    <AvatarFallback className="bg-gradient-to-br from-indigo-200 to-violet-200 text-xs font-bold text-indigo-800">
                      {getInitials(user?.name || user?.email || 'U')}
                    </AvatarFallback>
                  </Avatar>

                  {isProfileOpen ? (
                    <ChevronUp className="hidden size-4 text-text-muted sm:block" />
                  ) : (
                    <ChevronDown className="hidden size-4 text-text-muted sm:block" />
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col gap-0.5">
                    <span>{user?.name || 'User'}</span>
                    <span className="break-all text-xs font-normal text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="text-red-700 focus:bg-red-50 focus:text-red-700"
                  disabled={isLoading}
                  onSelect={handleLogout}
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav
        aria-label="Primary mobile"
        className="border-b border-black/[0.05] px-2 py-2 lg:hidden"
      >
        <div className="mx-auto flex w-full max-w-[1400px] gap-1 overflow-x-auto overflow-y-hidden rounded-2xl border border-black/[0.06] bg-white p-1 shadow-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {isPurchasePage ? (
            <PurchaseNavItems
              compact
              activePurchaseTab={activePurchaseTab}
              onTabClick={handlePurchaseTabClick}
              hasUnreadPOComments={hasUnreadPOComments}
            />
          ) : (
            <NavItems
              items={navItems}
              compact
              unreadCounts={unreadCounts}
              hasUnreadPOComments={hasUnreadPOComments}
            />
          )}
        </div>
      </nav>

      {/* Admin Chat Modal for Sales/Marketing/Purchase */}
      {canMessageAdmin && (
        <DirectMessageModal
          open={adminChatOpen}
          onOpenChange={setAdminChatOpen}
          partner={adminContactUser}
        />
      )}
    </div>
  )
}
